/**
 * Files kept for resuming an upload after its tab is gone.
 *
 * A File read from an <input> or a drop lives only as long as the page. Where
 * the browser also hands out a handle to it (the File System Access API, in
 * Chrome and Edge), the handle is stored in IndexedDB beside the image the file
 * is being uploaded into. A tab that closed or crashed mid-upload can then open
 * the same file again, with the person's permission asked by one click, instead
 * of asking them to find every file again. Elsewhere nothing is kept, and the
 * files are selected again as before.
 *
 * Kept per image, not per tab: an upload resumes from whichever tab opens its
 * study again. Dropped once the bytes are in, when the image is removed, after
 * the day an unfinished image lives on the server, and at sign-out. A slide of
 * several DICOM files (sent as one tar, tar.ts) keeps every file's handle under
 * its image, and comes back as all of them or not at all.
 */

type ReadPermission = { mode: 'read' };
export type KeptHandle = FileSystemFileHandle & {
  queryPermission?: (d: ReadPermission) => Promise<PermissionState>;
  requestPermission?: (d: ReadPermission) => Promise<PermissionState>;
};
/** One kept handle and what the file was when it was kept, so a changed file is not taken for it. */
export interface KeptHandleOf {
  handle: KeptHandle;
  name: string;
  size: number;
  lastModified: number;
  /** The name the file goes by in its slide's tar, when not its own (a MIRAX keeps its folder: tar.ts); given back by pathOf once reopened. */
  path?: string;
}
export interface KeptFile extends KeptHandleOf {
  imageId: string;
  at: number;
  /** A slide's files, every one; `handle` is then the first of them. */
  members?: KeptHandleOf[];
}

const DB_NAME = 'tr-upload-files';
const STORE = 'files';
/** A little longer than an unfinished image lives on the server (24 hours). */
const KEEP_MS = 26 * 3600_000;

/** The handle behind each File this page was given, where the browser gave one. */
const handleOf = new WeakMap<File, KeptHandle>();
/** Where a dropped file sits, from the folder that was dropped: `batch/Slide/Data0000.dat`. */
const paths = new WeakMap<File, string>();

/**
 * A file's path from the folder it was dropped in (or picked with), its own
 * name last; the bare name for a file on its own. What tells a MIRAX slide's
 * data folder from any other: it sits beside the .mrxs, named for it.
 */
export function pathOf(file: File): string {
  // A browser sets webkitRelativePath to '' (never undefined) on a file from an input, the picker or a handle.
  return paths.get(file) || (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
}

/** The folder part of a path (`batch/Slide` of `batch/Slide/Data0000.dat`); '' for a bare name. */
export const dirOf = (path: string): string => path.slice(0, Math.max(0, path.lastIndexOf('/')));

/**
 * The folder a file sits in, when it came in a drop or a pick of a folder;
 * null for a file on its own. Its OWN folder, not the top one dropped: a
 * scanner exports each slide as a folder named for it, and a drop of the
 * folder holding several of those names each slide, not the batch.
 */
export function folderOf(file: File): string | null {
  const parts = pathOf(file).split('/');
  return parts.length > 1 ? parts[parts.length - 2] || null : null;
}
/** Images this page kept a file for, or found one kept for: the only ones worth forgetting. */
const known = new Set<string>();

type Picker = (options?: { multiple?: boolean; types?: Array<{ description?: string; accept: Record<string, string[]> }> }) => Promise<KeptHandle[]>;
const picker = (): Picker | undefined => (typeof window === 'undefined' ? undefined : (window as unknown as { showOpenFilePicker?: Picker }).showOpenFilePicker);

/** Whether this browser can keep a file for later. */
export function canKeepFiles(): boolean {
  return !!picker() && typeof indexedDB !== 'undefined';
}

/**
 * Files chosen through the browser's own picker, each with its handle. Null
 * where there is no such picker (use the <input> instead); empty when the
 * person closed it. `accept` is the input's list of extensions.
 */
export async function pickFiles(accept: string): Promise<File[] | null> {
  const open = picker();
  if (!open) return null;
  const extensions = accept.split(',').map((e) => e.trim()).filter((e) => /^\.[a-z0-9]{1,15}$/i.test(e));
  let handles: KeptHandle[];
  try {
    handles = await open.call(window, {
      multiple: true,
      types: extensions.length ? [{ description: 'Images', accept: { 'application/octet-stream': extensions } }] : undefined,
    });
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return [];
    return null;
  }
  const files = await Promise.all(handles.map(async (h) => {
    const f = await h.getFile();
    handleOf.set(f, h);
    return f;
  }));
  return files;
}

/**
 * The files of a drop, each with its handle where the browser gives one. Call
 * it inside the drop handler itself: the drop's items are readable only then.
 *
 * A dropped FOLDER gives its files, however deep, each remembering where it
 * sits (pathOf, folderOf); only those whose names end in one of `accept` (the
 * input's list, `.svs,.dcm,…`), so its DICOMDIR and .DS_Store stay behind,
 * plus the files of a MIRAX slide's data folder, which sit beside the .mrxs
 * in a folder named for it and are nothing on their own (mirax.ts). A file
 * dropped on its own is taken whatever its name, as before. A MIRAX data
 * folder is read through its entry too, for what its handles leave out
 * (missedByHandles).
 */
export function droppedFiles(dt: DataTransfer, accept = ''): Promise<File[]> {
  const items = Array.from(dt.items ?? []).filter((i) => i.kind === 'file');
  if (!items.length) return Promise.resolve(Array.from(dt.files ?? []));
  type Item = DataTransferItem & {
    getAsFileSystemHandle?: () => Promise<FileSystemHandle | null>;
    webkitGetAsEntry?: () => FileSystemEntry | null;
  };
  // All three read now, inside the handler: the items are gone once it returns.
  const taken = items.map((i) => ({
    file: i.getAsFile(),
    handle: (i as Item).getAsFileSystemHandle?.() ?? null,
    entry: (i as Item).webkitGetAsEntry?.() ?? null,
  }));
  const wanted = acceptsName(accept);
  return Promise.all(taken.map(async ({ file, handle, entry }): Promise<Listed> => {
    const h = handle ? await handle.catch(() => null) : null;
    // Walked by its handles, with its entry kept beside them for what they leave out. A walk
    // that fails (a file the browser cannot open) falls back on the entry, as where there are no handles.
    const byHandles = h && h.kind === 'directory' ? await filesOfHandle(h as FileSystemDirectoryHandle).catch(() => null) : null;
    if (byHandles) return { got: byHandles.map(walked), entry: entry?.isDirectory ? (entry as FileSystemDirectoryEntry) : undefined };
    if (entry?.isDirectory) return { got: (await filesOfEntry(entry as FileSystemDirectoryEntry).catch(() => [])).map(walked) };
    if (file && h && h.kind === 'file') handleOf.set(file, h as KeptHandle);
    return { got: file ? [{ file, walked: false }] : [] };
  })).then(async (lists) => {
    const all = lists.flatMap((l) => l.got);
    // A MIRAX data folder: beside a .mrxs of the drop, named for it.
    const dataFolders = [...new Set(all.filter((x) => /\.mrxs$/i.test(x.file.name)).map((x) => pathOf(x.file).replace(/\.mrxs$/i, '')))];
    all.push(...(await missedByHandles(lists, dataFolders, all)).map(walked));
    const inDataFolder = (f: File) => {
      const dir = dirOf(pathOf(f));
      return dir !== '' && dataFolders.some((d) => dir === d || dir.startsWith(`${d}/`));
    };
    return all.filter((x) => !x.walked || (!x.file.name.startsWith('.') && (wanted(x.file.name) || inDataFolder(x.file)))).map((x) => x.file);
  });
}

/** A file of a drop, and whether it came from walking a dropped folder. */
type Walked = { file: File; walked: boolean };
const walked = (file: File): Walked => ({ file, walked: true });
/** What one dropped item gave: its files, and its entry when its folder was walked by handles. */
type Listed = { got: Walked[]; entry?: FileSystemDirectoryEntry };

/**
 * The files of the drop's MIRAX data folders that a walk by handles left out,
 * read again through the folder's entry. Chrome and Edge give no handle to a
 * file Safe Browsing rates dangerous, and leave it out of the folder; on
 * Windows that is every `.ini`, so a MIRAX slide lost its Slidedat.ini, without
 * which it cannot be read (2026-10-03, four slides). An entry lists every file.
 * Only MIRAX data folders are read twice. A file found this way has no handle,
 * so its slide is not kept (keepSlide: all or nothing) and is dropped again
 * after a reload, its bytes resuming.
 */
async function missedByHandles(lists: readonly Listed[], dataFolders: readonly string[], have: readonly Walked[]): Promise<File[]> {
  const seen = new Set(have.map((x) => pathOf(x.file)));
  const out: File[] = [];
  for (const { entry } of lists) {
    if (!entry) continue;
    for (const d of dataFolders) {
      // The dropped folder is the data folder, or holds it.
      if (d !== entry.name && !d.startsWith(`${entry.name}/`)) continue;
      const dir = await entryAt(entry, d.split('/').slice(1)).catch(() => null);
      if (!dir) continue;
      for (const f of await filesOfEntry(dir, d).catch(() => [])) {
        if (seen.has(pathOf(f))) continue;
        seen.add(pathOf(f));
        out.push(f);
      }
    }
  }
  return out;
}

/** Whether a file's name is one an input's `accept` list takes; every name when it lists none. */
function acceptsName(accept: string): (name: string) => boolean {
  const exts = accept.split(',').map((e) => e.trim().toLowerCase()).filter((e) => e.startsWith('.'));
  return (name) => !exts.length || exts.some((e) => name.toLowerCase().endsWith(e));
}

/** A folder's files, however deep, each with its handle (Chrome, Edge) and its path from the folder. */
async function filesOfHandle(dir: FileSystemDirectoryHandle): Promise<File[]> {
  const out: File[] = [];
  const walk = async (d: FileSystemDirectoryHandle, at: string) => {
    for await (const child of (d as FileSystemDirectoryHandle & { values: () => AsyncIterable<FileSystemHandle> }).values()) {
      if (child.kind === 'directory') await walk(child as FileSystemDirectoryHandle, `${at}/${child.name}`);
      else {
        const file = await (child as KeptHandle).getFile();
        handleOf.set(file, child as KeptHandle);
        paths.set(file, `${at}/${file.name}`);
        out.push(file);
      }
    }
  };
  await walk(dir, dir.name);
  return out;
}

/** A folder's entries, every one: a reader answers in batches (100 in Chrome) until an empty one. */
const readAll = (reader: FileSystemDirectoryReader) => new Promise<FileSystemEntry[]>((resolve, reject) => {
  const all: FileSystemEntry[] = [];
  const next = () => reader.readEntries((batch) => (batch.length ? (all.push(...batch), next()) : resolve(all)), reject);
  next();
});

/** The folder at `names` inside `dir`, one name per level; null when there is none. */
async function entryAt(dir: FileSystemDirectoryEntry, names: readonly string[]): Promise<FileSystemDirectoryEntry | null> {
  let at = dir;
  for (const name of names) {
    const next = (await readAll(at.createReader())).find((c) => c.isDirectory && c.name === name);
    if (!next) return null;
    at = next as FileSystemDirectoryEntry;
  }
  return at;
}

/**
 * A folder's files, however deep, read through its entry: where the browser
 * gives no handles, and for what handles leave out (missedByHandles). Each
 * with its path from the folder, or from `from` when the folder sits inside a
 * dropped one.
 */
async function filesOfEntry(dir: FileSystemDirectoryEntry, from = dir.name): Promise<File[]> {
  const out: File[] = [];
  const walk = async (d: FileSystemDirectoryEntry, at: string) => {
    for (const child of await readAll(d.createReader())) {
      if (child.isDirectory) await walk(child as FileSystemDirectoryEntry, `${at}/${child.name}`);
      else {
        const file = await new Promise<File>((resolve, reject) => (child as FileSystemFileEntry).file(resolve, reject));
        paths.set(file, `${at}/${file.name}`);
        out.push(file);
      }
    }
  };
  await walk(dir, from);
  return out;
}

/** One connection for the page's life, opened on first use (and again if it is lost). */
let db: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  db ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'imageId' });
    };
    req.onsuccess = () => {
      req.result.onclose = () => { db = null; };
      req.result.onversionchange = () => { req.result.close(); db = null; };
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
  }).catch((err) => {
    db = null;
    throw err;
  });
  return db;
}

async function withStore<T>(mode: IDBTransactionMode, use: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  if (typeof indexedDB === 'undefined') return undefined;
  const conn = await openDb();
  return new Promise<T | undefined>((resolve, reject) => {
    const tx = conn.transaction(STORE, mode);
    const req = use(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req ? req.result : undefined);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

const keptOf = (file: File): KeptHandleOf | null => {
  const handle = handleOf.get(file);
  return handle ? { handle, name: file.name, size: file.size, lastModified: file.lastModified } : null;
};

/** Keep the file an image is being uploaded from, when the browser gave a handle to it. */
export async function keepFile(imageId: string, file: File): Promise<void> {
  const k = keptOf(file);
  if (!k || !canKeepFiles()) return;
  const kept: KeptFile = { imageId, ...k, at: Date.now() };
  known.add(imageId);
  await withStore('readwrite', (s) => s.put(kept)).catch(() => undefined);
}

/** Keep the files a slide is being uploaded from (tar.ts), with the names they go by in its tar: all of them, or nothing. */
export async function keepSlide(imageId: string, files: readonly File[], paths?: readonly string[]): Promise<void> {
  const members = files.map((f, i) => {
    const k = keptOf(f);
    return k && paths?.[i] && paths[i] !== f.name ? { ...k, path: paths[i] } : k;
  });
  if (!files.length || !members.every((k): k is KeptHandleOf => !!k) || !canKeepFiles()) return;
  const kept: KeptFile = { imageId, ...members[0], members, at: Date.now() };
  known.add(imageId);
  await withStore('readwrite', (s) => s.put(kept)).catch(() => undefined);
}

/**
 * The image's bytes are in, or it is gone: nothing to resume. Only an image a
 * file was kept for is looked up; one chosen through the plain input has
 * nothing to forget. (What a page never learned of goes with age, keptFiles.)
 */
export async function forgetFile(imageId: string): Promise<void> {
  if (!known.delete(imageId) || !canKeepFiles()) return;
  await withStore('readwrite', (s) => s.delete(imageId)).catch(() => undefined);
}

/** Sign-out: the next account on this browser inherits nothing. */
export async function forgetAllFiles(): Promise<void> {
  known.clear();
  if (!canKeepFiles()) return;
  await withStore('readwrite', (s) => s.clear()).catch(() => undefined);
}

/** What is kept for these images. Anything older than an unfinished image lives is dropped on the way. */
export async function keptFiles(imageIds: readonly string[]): Promise<Map<string, KeptFile>> {
  const found = new Map<string, KeptFile>();
  if (!imageIds.length || !canKeepFiles()) return found;
  const all = await withStore<KeptFile[]>('readonly', (s) => s.getAll() as IDBRequest<KeptFile[]>).catch(() => undefined);
  const stale: string[] = [];
  for (const k of all ?? []) {
    if (Date.now() - k.at > KEEP_MS) stale.push(k.imageId);
    else if (imageIds.includes(k.imageId)) {
      found.set(k.imageId, k);
      known.add(k.imageId);
    }
  }
  if (stale.length) await withStore('readwrite', (s) => { stale.forEach((id) => s.delete(id)); }).catch(() => undefined);
  return found;
}

/** What a kept image opens again as: its file, or a slide's files (all of them). */
export type Reopened = File | File[] | 'ask' | null;

/**
 * Kept files, opened again. `ask` may show the browser's permission prompts, so
 * only a click passes it. Each answer is the file (a slide's files, every one);
 * 'ask' when its permission is still to be given by a click; null when it was
 * refused, or the file is gone or changed since (then it is selected again by
 * hand). A slide one file of which cannot be opened is null as a whole: sent
 * as one tar, its bytes are all its files' or nothing.
 *
 * Every prompt is started inside the click, before anything is awaited: asked
 * one after another, the first prompt spends the click and each other file
 * would take a click of its own.
 */
export function reopenFiles(kept: readonly KeptFile[], ask: boolean): Promise<Reopened[]> {
  const handles = kept.map((k) => (k.members ?? [k]));
  const asked = handles.map((hs) => hs.map((h) =>
    ask && h.handle.requestPermission
      ? h.handle.requestPermission({ mode: 'read' }).then((state) => state, (err: unknown) => err)
      : null,
  ));
  const open = async (h: KeptHandleOf, prompted: Promise<unknown> | null): Promise<File | 'ask' | null> => {
    try {
      const answer = prompted ? await prompted : h.handle.queryPermission ? await h.handle.queryPermission({ mode: 'read' }) : 'granted';
      // A prompt the browser would not show without a fresh click: another click asks again.
      if ((answer as { name?: string } | null)?.name === 'SecurityError') return 'ask';
      if (answer === 'prompt') return 'ask';
      if (answer !== 'granted') return null;
      const file = await h.handle.getFile();
      if (file.name !== h.name || file.size !== h.size || file.lastModified !== h.lastModified) return null;
      handleOf.set(file, h.handle);
      if (h.path) paths.set(file, h.path);
      return file;
    } catch {
      // Moved or deleted (NotFoundError).
      return null;
    }
  };
  return Promise.all(kept.map(async (k, i): Promise<Reopened> => {
    const opened = await Promise.all(handles[i].map((h, j) => open(h, asked[i][j])));
    if (!k.members) return opened[0];
    if (opened.some((f) => f === 'ask')) return 'ask';
    return opened.every((f): f is File => f instanceof File) ? opened : null;
  }));
}

/** One kept file, opened again (reopenFiles). */
export async function reopenFile(kept: KeptFile, ask: boolean): Promise<Reopened> {
  return (await reopenFiles([kept], ask))[0];
}
